import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import "./CapturableSlide.css";

const waitForImages = (element) => {
    const images = Array.from(element.querySelectorAll("img"));

    return Promise.all(
        images.map((image) => {
            if (image.complete && image.naturalWidth > 0) {
                return Promise.resolve();
            }

            return new Promise((resolve) => {
                image.addEventListener("load", resolve, { once: true });
                image.addEventListener("error", resolve, { once: true });
            });
        })
    );
};

const safeFileName = (value) => {
    const normalized = String(value || "service-card")
        .trim()
        .replace(/[<>:"/\\|?*]/g, "-")
        .replace(/\s+/g, "-");

    return normalized || "service-card";
};

const CapturableSlide = ({
    children,
    background = "transparent",
    fileName = "service-card",
    captureEnabled = true,
}) => {
    const slideRef = useRef(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [captureError, setCaptureError] = useState("");
    const [captureSuccess, setCaptureSuccess] = useState("");

    const captureCard = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const slide = slideRef.current;
        if (!slide || isCapturing) return;

        setIsCapturing(true);
        setCaptureError("");
        setCaptureSuccess("");

        try {
            if (document.fonts?.ready) {
                await document.fonts.ready;
            }

            await waitForImages(slide);

            const rect = slide.getBoundingClientRect();
            const computedBackground =
                window.getComputedStyle(slide).backgroundColor;
            const dataUrl = await toPng(slide, {
                cacheBust: true,
                pixelRatio: 2,
                width: Math.ceil(rect.width),
                height: Math.ceil(rect.height),
                backgroundColor: computedBackground,
                filter: (node) =>
                    node?.dataset?.captureIgnore !== "true",
            });

            if (!dataUrl) {
                throw new Error("Card image could not be created.");
            }

            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = `${safeFileName(fileName)}.png`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setCaptureSuccess("تم حفظ صورة البطاقة.");
            window.setTimeout(() => setCaptureSuccess(""), 2500);
        } catch (error) {
            setCaptureError("تعذر حفظ صورة البطاقة. حاول مرة أخرى.");
        } finally {
            setIsCapturing(false);
        }
    };

    return (
        <div
            ref={slideRef}
            className="LpSlide"
            style={{ "--lp-slide-background": background }}
        >
            {children}

            {captureEnabled && (
                <button
                    type="button"
                    className="LpSlide__capture"
                    onClick={captureCard}
                    disabled={isCapturing}
                    data-capture-ignore="true"
                    aria-label={
                        isCapturing
                            ? "جاري تجهيز صورة البطاقة"
                            : "حفظ البطاقة كصورة"
                    }
                    title="حفظ البطاقة كصورة"
                >
                    <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="LpSlide__capture-icon"
                    >
                        <path d="M9 3 7.2 5H4a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h16a3 3 0 0 0 3-3V8a3 3 0 0 0-3-3h-3.2L15 3H9Zm3 5a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2.2a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 0 0 0-5.6Z" />
                    </svg>
                    <span>{isCapturing ? "جاري الحفظ" : "حفظ الصورة"}</span>
                </button>
            )}

            {captureError && (
                <p
                    className="LpSlide__capture-error"
                    role="alert"
                    data-capture-ignore="true"
                >
                    {captureError}
                </p>
            )}

            {captureSuccess && (
                <p
                    className="LpSlide__capture-success"
                    role="status"
                    data-capture-ignore="true"
                >
                    {captureSuccess}
                </p>
            )}
        </div>
    );
};

export default CapturableSlide;
